import { IonList, IonRouterLink } from '@ionic/react';
import { Form, Formik } from 'formik';
import { ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useInitiateAuth, useLogout } from '../../api/auth.hooks';
import { useDirectConnectCachedMachines } from '../../direct-connect/cache/direct-connect-machine-cache';
import { useBeaconRouter } from '../../route/route.hooks';
import { logger } from '../../util/logger';
import { useQueryParameters } from '../../util/router';
import { DownloadApp } from '../appstore/AppDownload';
import { FormSubmitButton } from '../form/form-submit.button';
import { IonFormField } from '../form/ion-form.field';
import { SaveStatusToast } from '../toast/toast.gql';
import { LoginWrapper } from './login-wrapper';
import './login.css';

interface Form {
  email?: string;
}

const Login = (): ReactElement => {
  const logout = useLogout();
  const { builder } = useBeaconRouter();
  const { i18n } = useTranslation();
  const signInSchema = yup.object().shape({
    email: yup
      .string()
      .email(i18n.t('common:login_invalid_email'))
      .required('*'),
  });

  const [initiateAuth, { loading, error }] = useInitiateAuth();

  const queryParams = useQueryParameters();
  const email = queryParams.get('email') || undefined;
  const tenantId = queryParams.get('tenantId') || undefined;
  const forceLogout = queryParams.get('forceLogout') || undefined;

  const onSubmit = async (form: Form) => {
    if (form.email) {
      logger.info('initiating auth', {
        email: form.email,
        tenantId,
      });
      initiateAuth({
        variables: {
          input: {
            email: form.email,
            tenantId,
          },
        },
      });
    }
  };

  useEffect(() => {
    if (forceLogout) {
      logout();
    }
  }, [forceLogout, logout]);

  useEffect(() => {
    if (error !== undefined) {
      logger.warn('failed to initiate auth', {
        errorMessage: error.message,
        email,
        tenantId,
      });
    }
  }, [email, error, tenantId]);

  const initialValues: Form = {
    email,
  };

  const directConnectMachines = useDirectConnectCachedMachines();

  return (
    <LoginWrapper>
      <Formik
        validationSchema={signInSchema}
        initialValues={initialValues}
        onSubmit={onSubmit}
        validateOnMount={true}
      >
        {({ isValid }) => {
          return (
            <>
              <Form>
                <IonList lines="none">
                  <IonFormField
                    label={i18n.t('common:email')}
                    name="email"
                    inputProps={{
                      type: 'email',
                      autocomplete: 'email',
                      autofocus: true,
                    }}
                  />
                </IonList>
                <br />
                <FormSubmitButton
                  loading={loading}
                  disabled={!isValid}
                  label={i18n.t('common:login')}
                  placement="center"
                />
                <br />
                <div style={{ textAlign: 'center' }}>
                  <IonRouterLink routerLink={builder.getAuthSignupRoute()}>
                    {i18n.t('common:create_organization')}
                  </IonRouterLink>
                </div>
                <br />
                {directConnectMachines.length > 0 && (
                  <div style={{ textAlign: 'center' }}>
                    <IonRouterLink
                      routerLink={builder.getDirectConnectSelectMachineRoute()}
                    >
                      {i18n.t('common:direct_connect')}
                    </IonRouterLink>
                  </div>
                )}
                <DownloadApp />
              </Form>
              <SaveStatusToast
                error={error}
                loading={loading}
                defaultErrorMessageKey={'common:login_failed'}
              />
            </>
          );
        }}
      </Formik>
    </LoginWrapper>
  );
};

export { Login };
